import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { VENUE_SHORT_OPTIONS } from "../util/classDisplay";
import { blockedReasonCode } from "../util/stationCard";
import useVenueDateTime from "../hooks/useVenueDateTime";

// Placeholder glyph for a value the server did not send (U+2014), escaped so source stays dash-free.
const EMPTY_CELL = "\u2014";

// Plain words for why a send was refused. One literal key per code (no dynamic
// keys), so the i18n extractor and the unused-key check can see every one.
function whyText(code, t) {
  switch (code) {
    case "ride_scratched":
      return t("stationMonitor.blockedWhy.ride_scratched");
    case "ride_ended":
      return t("stationMonitor.blockedWhy.ride_ended");
    case "duplicate_score_sheet":
      return t("stationMonitor.blockedWhy.duplicate_score_sheet");
    case "term_reason_conflict":
      return t("stationMonitor.blockedWhy.term_reason_conflict");
    case "marks_confirmed_frozen":
      return t("stationMonitor.blockedWhy.marks_confirmed_frozen");
    case "sheet_completed":
      return t("stationMonitor.blockedWhy.sheet_completed");
    case "marks_not_confirmed":
      return t("stationMonitor.blockedWhy.marks_not_confirmed");
    case "panel_not_fully_signed":
      return t("stationMonitor.blockedWhy.panel_not_fully_signed");
    case "sheet_editing":
      return t("stationMonitor.blockedWhy.sheet_editing");
    case "concurrent_modification":
      return t("stationMonitor.blockedWhy.concurrent_modification");
    case "stale_write":
      return t("stationMonitor.blockedWhy.stale_write");
    case "not_assigned":
      return t("stationMonitor.blockedWhy.not_assigned");
    case "forbidden":
      return t("stationMonitor.blockedWhy.forbidden");
    case "not_found":
      return t("stationMonitor.blockedWhy.not_found");
    case "test_mismatch":
      return t("stationMonitor.blockedWhy.test_mismatch");
    case "missing_signature":
      return t("stationMonitor.blockedWhy.missing_signature");
    case "judge_not_on_panel":
      return t("stationMonitor.blockedWhy.judge_not_on_panel");
    case "validation_error":
      return t("stationMonitor.blockedWhy.validation_error");
    default:
      return t("stationMonitor.blockedWhy.other");
  }
}

function whatText(code, t) {
  switch (code) {
    case "ride_scratched":
      return t("stationMonitor.blockedWhat.ride_scratched");
    case "ride_ended":
      return t("stationMonitor.blockedWhat.ride_ended");
    case "duplicate_score_sheet":
      return t("stationMonitor.blockedWhat.duplicate_score_sheet");
    case "term_reason_conflict":
      return t("stationMonitor.blockedWhat.term_reason_conflict");
    case "marks_confirmed_frozen":
      return t("stationMonitor.blockedWhat.marks_confirmed_frozen");
    case "sheet_completed":
      return t("stationMonitor.blockedWhat.sheet_completed");
    case "marks_not_confirmed":
      return t("stationMonitor.blockedWhat.marks_not_confirmed");
    case "panel_not_fully_signed":
      return t("stationMonitor.blockedWhat.panel_not_fully_signed");
    case "sheet_editing":
      return t("stationMonitor.blockedWhat.sheet_editing");
    case "concurrent_modification":
      return t("stationMonitor.blockedWhat.concurrent_modification");
    case "stale_write":
      return t("stationMonitor.blockedWhat.stale_write");
    case "not_assigned":
      return t("stationMonitor.blockedWhat.not_assigned");
    case "forbidden":
      return t("stationMonitor.blockedWhat.forbidden");
    case "not_found":
      return t("stationMonitor.blockedWhat.not_found");
    case "test_mismatch":
      return t("stationMonitor.blockedWhat.test_mismatch");
    case "missing_signature":
      return t("stationMonitor.blockedWhat.missing_signature");
    case "judge_not_on_panel":
      return t("stationMonitor.blockedWhat.judge_not_on_panel");
    case "validation_error":
      return t("stationMonitor.blockedWhat.validation_error");
    default:
      return t("stationMonitor.blockedWhat.other");
  }
}

function classLabel(sheet) {
  const label = [sheet.classNumber, sheet.className].filter(Boolean).join(" ");
  return label || EMPTY_CELL;
}

function Row({ label, children, testId }) {
  return (
    <>
      <Typography component="dt" variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography component="dd" variant="body2" color="text.primary" sx={{ m: 0, overflowWrap: "anywhere" }} data-testid={testId}>
        {children}
      </Typography>
    </>
  );
}

// #3548: the sheets behind a "Send blocked" box. One entry per blockedSheets item
// (#3547): CNO, Class, Ride time, Why and What to do in plain words. `target` is
// { group, block } of the box that was pressed, or null when closed.
export default function SendBlockedDialog({ target, onClose, t }) {
  const formatShort = useVenueDateTime(VENUE_SHORT_OPTIONS);
  const sheets = target?.block?.snapshot?.blockedSheets ?? [];
  const count = sheets.length;
  const stationName = target?.group?.stationName ?? t("stationMonitor.stationUnnamed");
  const scribeName = target?.block?.scribeName || t("common.unknownUser");
  return (
    <Dialog open={target !== null} onClose={onClose} fullWidth maxWidth="sm" scroll="paper" data-testid="station-monitor-send-blocked-dialog">
      <DialogTitle component="div">
        <Typography variant="h6" component="h2">
          {t("stationMonitor.sendBlocked.title", { count })}
        </Typography>
        <Typography variant="body2" color="text.secondary" data-testid="station-monitor-send-blocked-subtitle">
          {t("stationMonitor.sendBlocked.subtitle", { station: stationName, scribe: scribeName })}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 2 }}>
          {sheets.map((sheet, i) => {
            const code = blockedReasonCode(sheet.failureCode);
            return (
              <Box
                component="li"
                key={`${sheet.rideId ?? "ride"}-${i}`}
                sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.5 }}
                data-testid="station-monitor-send-blocked-sheet"
              >
                <Box
                  component="dl"
                  sx={{ m: 0, display: "grid", gridTemplateColumns: "max-content minmax(0, 1fr)", columnGap: 2, rowGap: 0.5 }}
                >
                  <Row label={t("stationMonitor.sendBlocked.cno")}>{sheet.cno || EMPTY_CELL}</Row>
                  <Row label={t("stationMonitor.sendBlocked.class")}>{classLabel(sheet)}</Row>
                  <Row label={t("stationMonitor.sendBlocked.rideTime")}>{sheet.rideTime ? formatShort(sheet.rideTime) || EMPTY_CELL : EMPTY_CELL}</Row>
                  <Row label={t("stationMonitor.sendBlocked.why")}>{whyText(code, t)}</Row>
                  <Row label={t("stationMonitor.sendBlocked.whatToDo")}>{whatText(code, t)}</Row>
                </Box>
              </Box>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="station-monitor-send-blocked-close">
          {t("stationMonitor.sendBlocked.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
